/**
 * Gateway → Tenants card (VSNP-1126).
 *
 * The license/tenancy face of a gateway detail page. Dedicated: a read-only
 * line naming the owning tenant (D4: fixed forever). Shared: the assignment
 * table (slot / lane / status) with SuperAdmin-only assign & remove (D3),
 * mirroring the MTGE tenant-assignment pattern.
 *
 * The GET /gateways/:id/tenants endpoint is SuperAdmin-only, so this card only
 * fetches for SuperAdmins; a tenant admin just sees the mode summary.
 */

import { useCallback, useEffect, useState } from 'react';
import { Building2, Plus, Trash2 } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/ToastContainer';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { USER_TYPES } from '../../utils/constants';
import accessService, { type AccessGateway, type GatewayTenantAssignment } from '../../services/access.service';

interface GatewayTenantsCardProps {
  gateway: AccessGateway;
  /** Called after an assign/remove so the parent can refresh counters. */
  onChanged: () => void;
}

const GatewayTenantsCard = ({ gateway, onChanged }: GatewayTenantsCardProps) => {
  const toast = useToast();
  const user = useAuthStore((state) => state.user);
  const tenants = useTenantStore((state) => state.tenants);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;

  const [assignments, setAssignments] = useState<GatewayTenantAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickerValue, setPickerValue] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<GatewayTenantAssignment | null>(null);
  const [removing, setRemoving] = useState(false);

  const shared = gateway.sharing_mode === 'shared';
  const tenantName = (id: string | null) => tenants.find((t) => t.id === id)?.name ?? id ?? '—';

  const fetchAssignments = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      setLoading(true);
      setAssignments((await accessService.listGatewayTenants(gateway.id)) ?? []);
    } catch {
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [gateway.id, isSuperAdmin]);

  useEffect(() => {
    void fetchAssignments();
  }, [fetchAssignments]);

  const handleAssign = async () => {
    if (!pickerValue) return;
    try {
      setAssigning(true);
      await accessService.assignGatewayTenant(gateway.id, pickerValue);
      toast.success('Tenant assigned', tenantName(pickerValue));
      setPickerValue('');
      await fetchAssignments();
      onChanged();
    } catch (err) {
      toast.error('Assign failed', err instanceof Error ? err.message : 'Could not assign the tenant');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      await accessService.removeGatewayTenant(gateway.id, removeTarget.tenant_id);
      toast.success('Tenant removed', tenantName(removeTarget.tenant_id));
      setRemoveTarget(null);
      await fetchAssignments();
      onChanged();
    } catch (err) {
      toast.error('Remove failed', err instanceof Error ? err.message : 'Could not remove the tenant');
    } finally {
      setRemoving(false);
    }
  };

  const assignedIds = new Set(assignments.map((a) => a.tenant_id));
  const candidates = tenants.filter((t) => !assignedIds.has(t.id));
  const atCapacity = gateway.current_tenants >= gateway.max_tenants;

  return (
    <div className="card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Building2 className="h-4 w-4 text-teal-600" /> Tenants
          <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${shared ? 'bg-info-100 text-info-700' : 'bg-gray-100 text-gray-700'}`}>
            {gateway.sharing_mode}
          </span>
        </h2>
        {shared && (
          <span className="text-xs text-gray-400">
            {gateway.current_tenants}/{gateway.max_tenants} assigned
          </span>
        )}
      </div>

      {!shared ? (
        <p className="text-sm text-gray-600">
          Dedicated to <span className="font-medium">{tenantName(gateway.owner_tenant_id)}</span>. The license type is
          fixed at creation — repurposing this node requires a factory reset and re-registration with a new token.
        </p>
      ) : !isSuperAdmin ? (
        <p className="text-sm text-gray-600">Shared gateway. Tenant assignments are managed by the platform operator.</p>
      ) : (
        <>
          {isSuperAdmin && (
            <div className="mb-3 flex items-center gap-2">
              <select
                className="input-base flex-1"
                value={pickerValue}
                onChange={(e) => setPickerValue(e.target.value)}
                disabled={assigning || atCapacity}
              >
                <option value="" disabled>
                  {atCapacity ? 'At capacity — raise max tenants to add more' : 'Assign a tenant…'}
                </option>
                {candidates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void handleAssign()}
                disabled={!pickerValue || assigning || atCapacity}
              >
                <Plus className="mr-1 inline h-4 w-4" />
                {assigning ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          )}

          {loading ? (
            <p className="text-sm text-gray-500">Loading assignments…</p>
          ) : assignments.length === 0 ? (
            <p className="text-sm text-gray-500">
              No tenants assigned yet. This gateway serves no one until a tenant is assigned.
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                  <th className="py-2 pr-4">Tenant</th>
                  <th className="py-2 pr-4">Slot</th>
                  <th className="py-2 pr-4">Lane</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium text-gray-900">{tenantName(a.tenant_id)}</td>
                    <td className="py-2 pr-4 text-gray-600">{a.tenant_slot}</td>
                    <td className="py-2 pr-4 text-gray-600">{a.isolation_id}</td>
                    <td className="py-2 pr-4"><StatusBadge status={a.status} /></td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="p-1 text-gray-400 hover:text-danger-600"
                        title="Remove tenant from this gateway"
                        onClick={() => setRemoveTarget(a)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        title="Remove tenant from gateway"
        message={`Remove '${tenantName(removeTarget?.tenant_id ?? null)}' from '${gateway.name ?? gateway.node_id}'? Its clients lose this PoP and its policy bundle stops being delivered here.`}
        confirmText="Remove"
        variant="danger"
        isLoading={removing}
      />
    </div>
  );
};

export default GatewayTenantsCard;
